import { ActivityParentEntityId, NoteId } from "@sp-crm/core";
import { ActivityFeed } from "components/activity-feed/activity-feed";
import { ActivityFeedNote } from "components/activity-feed/activity-feed-note";
import {
    ActivityEntityType,
    EntityActivityEntry,
} from "components/entity-activity/types";
import {
    ActivityType,
    GetNotesForEntityQuery,
    UpdateNoteMutation,
} from "generated/graphql";
import React from "react";
import { UnifiedTimelineActivityEntry } from "./unified-timeline-activity-entry";

type NoteDto = GetNotesForEntityQuery["getNotesForEntity"][0];

interface TimelineItem {
    key: string;
    timestamp: Date;
    type: "note" | "activity";
    note?: NoteDto;
    activity?: EntityActivityEntry;
}

interface UnifiedTimelineFeedProps {
    entityId: ActivityParentEntityId;
    entityType: ActivityEntityType;
    activities: EntityActivityEntry[];
    notes: NoteDto[];
    refetch: () => void;
    onNoteDeleted: (noteId: NoteId) => void;
    onNoteUpdated: (note: UpdateNoteMutation["updateNote"]) => void;
    localDeletes: NoteId[];
    pinnedNoteId: NoteId | null;
}

const NOTE_ACTIVITY_TYPES = new Set([
    ActivityType.NoteCreate,
    ActivityType.NoteUpdate,
    ActivityType.NoteDelete,
]);

export const UnifiedTimelineFeed: React.FC<UnifiedTimelineFeedProps> = props => {
    const {
        entityId,
        entityType,
        activities,
        notes,
        refetch,
        onNoteDeleted,
        onNoteUpdated,
        localDeletes,
        pinnedNoteId,
    } = props;

    const items = React.useMemo(() => {
        const result: TimelineItem[] = [];

        notes
            .filter(n => !n.isDraft && !localDeletes.includes(n._id as NoteId))
            .forEach(note => {
                result.push({
                    key: `note-${note._id}`,
                    timestamp: new Date(note.dateAdded),
                    type: "note",
                    note,
                });
            });

        activities
            .filter(entry => !NOTE_ACTIVITY_TYPES.has(entry.activity.activityType))
            .forEach(entry => {
                result.push({
                    key: `activity-${entry.activity.id}`,
                    timestamp: new Date(entry.activity.createdAt),
                    type: "activity",
                    activity: entry,
                });
            });

        result.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

        return result;
    }, [activities, notes, localDeletes]);

    return (
        <ActivityFeed>
            {items.map(item => {
                if (item.type === "note" && item.note) {
                    return (
                        <ActivityFeedNote
                            key={item.key}
                            entityId={entityId}
                            refetch={refetch}
                            note={item.note}
                            pinnedNoteId={pinnedNoteId}
                            onDeleted={onNoteDeleted}
                            onUpdated={onNoteUpdated}
                        />
                    );
                }
                if (item.type === "activity" && item.activity) {
                    return (
                        <UnifiedTimelineActivityEntry
                            key={item.key}
                            entityId={entityId}
                            entityType={entityType}
                            activity={item.activity}
                        />
                    );
                }
                return null;
            })}
        </ActivityFeed>
    );
};
